import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { apiUrl } from '../config/api';
import { getApiErrorMessage } from '../utils/errorMessage';
import './Login.css';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (username.trim() === '' || password.trim() === '') {
      setError('Please enter both username and password.');
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch(apiUrl('/user/admin/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(getApiErrorMessage(data, res.status, { 401: 'Invalid username or password.' }));
        setIsLoading(false);
        return;
      }

      // Extract token from response (supports { token } or { data: { token } })
      const token = (data && (data.token || (data.data && data.data.token))) || '';
      if (!token) {
        setError('Login succeeded but no token was returned by the server.');
        setIsLoading(false);
        return;
      }

      localStorage.setItem('token', token);
      login();
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Unable to connect. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src={`${process.env.PUBLIC_URL || ''}/logo.png`} alt="BabyBillion" className="login-logo" />
          <h1>BabyBillion</h1>
          <p>Admin Console</p>
        </div>
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Username <span className="label-required" aria-hidden="true">*</span></label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              autoComplete="username"
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password <span className="label-required" aria-hidden="true">*</span></label>
            <div className="login-password-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={isLoading}
                className="login-password-input"
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                disabled={isLoading}
                title={showPassword ? 'Hide password' : 'Show password'}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? (
                  <FiEyeOff className="login-password-icon" aria-hidden />
                ) : (
                  <FiEye className="login-password-icon" aria-hidden />
                )}
              </button>
            </div>
          </div>
          {error && <div className="error-message" role="alert">{error}</div>}
          <button 
            type="submit" 
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Login;

